import React, { useEffect, useState } from 'react';
import { adminApi } from './api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Gift, Plus, Trash2, Mail, Calendar as CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../lib/logger';

const DEFAULT_FORM = {
  email: '',
  tier: 'pro',
  months: 3,
  package: '3-session',
  note: '',
};

function packageToMonths(pkg) {
  if (pkg === '3-session') return 3;
  if (pkg === '6-session') return 6;
  return null;
}

function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86400000));
}

export default function AdminGrants() {
  const [grants, setGrants] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { grants: g } = await adminApi.listGrants();
      setGrants(g || []);
    } catch (e) {
      logger.error('admin grants load failed', e);
      toast.error(e.message || 'Could not load grants.');
      setGrants([]);
    }
  };

  useEffect(() => { refresh(); }, []);

  const setField = (k, v) => {
    setForm((s) => {
      const next = { ...s, [k]: v };
      if (k === 'package') {
        const m = packageToMonths(v);
        if (m !== null) next.months = m;
      }
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) { toast.error('Email is required.'); return; }
    setBusy(true);
    try {
      const g = await adminApi.createGrant({
        email: form.email.trim().toLowerCase(),
        tier: form.tier,
        months: Number(form.months) || 1,
        package: form.package,
        note: form.note.trim() || undefined,
      });
      setGrants((prev) => [g, ...(prev || [])]);
      setForm(DEFAULT_FORM);
      toast.success(`Granted ${g.months} months of ${g.tier} to ${g.user_email}.`);
    } catch (err) {
      logger.error('grant create failed', err);
      toast.error(err.message || 'Could not create grant.');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (grant) => {
    if (!window.confirm(`Revoke ${grant.user_email}'s app access? They'll drop back to Free.`)) return;
    try {
      await adminApi.revokeGrant(grant.grant_id);
      setGrants((prev) => prev.map((g) => g.grant_id === grant.grant_id ? { ...g, status: 'revoked', revoked_at: new Date().toISOString() } : g));
      toast.success('Grant revoked.');
    } catch (err) {
      logger.error('grant revoke failed', err);
      toast.error(err.message || 'Could not revoke grant.');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-grants-page">
      <div className="flex items-center gap-2">
        <Gift className="h-4 w-4 text-[#004C54]" />
        <h2 className="font-heading text-lg font-bold">Coaching package grants</h2>
        {grants && <Badge variant="outline" className="border-foreground/15 text-foreground/60">{grants.length}</Badge>}
      </div>
      <p className="text-sm text-foreground/65 max-w-2xl">
        Comp app access to clients who buy a 1:1 session package outside of Stripe (e.g. via Calendly + invoice). They'll need to create an account first — with the same email — then come back here to grant them N months.
      </p>

      <Card className="border-border" data-testid="grant-form-card">
        <CardContent className="p-5 sm:p-6">
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.4fr_140px_100px_140px_auto] gap-3 items-end" data-testid="grant-form">
            <div className="min-w-0">
              <Label htmlFor="grant-email" className="text-[12px] text-foreground/70 mb-1.5 block">Client email</Label>
              <Input id="grant-email" type="email" placeholder="client@example.com"
                value={form.email} onChange={(e) => setField('email', e.target.value)}
                disabled={busy} data-testid="grant-email" required />
            </div>
            <div>
              <Label className="text-[12px] text-foreground/70 mb-1.5 block">Package</Label>
              <Select value={form.package} onValueChange={(v) => setField('package', v)}>
                <SelectTrigger data-testid="grant-package"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="3-session">3-session pack</SelectItem>
                  <SelectItem value="6-session">6-session pack</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[12px] text-foreground/70 mb-1.5 block">Months</Label>
              <Input type="number" min={1} max={24} value={form.months}
                onChange={(e) => setField('months', e.target.value)} disabled={busy} data-testid="grant-months" />
            </div>
            <div>
              <Label className="text-[12px] text-foreground/70 mb-1.5 block">Tier</Label>
              <Select value={form.tier} onValueChange={(v) => setField('tier', v)}>
                <SelectTrigger data-testid="grant-tier"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pro">Pro (Apex AI)</SelectItem>
                  <SelectItem value="premium">Premium (1:1)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={busy} className="bg-[#004C54] hover:bg-[#003a40] text-white h-10" data-testid="grant-submit">
              <Plus className="h-4 w-4 mr-1" /> {busy ? 'Granting…' : 'Grant access'}
            </Button>
            <div className="sm:col-span-2 lg:col-span-5">
              <Label className="text-[12px] text-foreground/70 mb-1.5 block">Note (optional)</Label>
              <Input placeholder="e.g. 6-session pack purchased 2026-05-15"
                value={form.note} onChange={(e) => setField('note', e.target.value)}
                disabled={busy} maxLength={240} data-testid="grant-note" />
            </div>
          </form>
        </CardContent>
      </Card>

      {grants === null && (
        <div className="space-y-2" data-testid="admin-grants-loading">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      )}

      {grants !== null && grants.length === 0 && (
        <Card className="border-border"><CardContent className="p-6 text-center text-foreground/60" data-testid="admin-grants-empty">
          No grants yet. Grant your first client app access using the form above.
        </CardContent></Card>
      )}

      {grants !== null && grants.length > 0 && (
        <Card className="border-border overflow-hidden">
          <div className="hidden sm:grid grid-cols-[1.4fr_120px_120px_140px_140px_auto] gap-3 px-4 py-2 bg-[#F6F7F8] text-[11px] uppercase tracking-wider text-foreground/60 font-semibold border-b border-border">
            <div>Client</div>
            <div>Package</div>
            <div>Tier</div>
            <div>Expires</div>
            <div>Status</div>
            <div className="text-right">Action</div>
          </div>
          <ul className="divide-y divide-border" data-testid="admin-grants-list">
            {grants.map((g) => {
              const daysLeft = daysUntil(g.expires_at);
              const expired = daysLeft !== null && daysLeft <= 0;
              const isActive = g.status === 'active' && !expired;
              return (
                <li key={g.grant_id} className="px-4 py-3 grid grid-cols-1 sm:grid-cols-[1.4fr_120px_120px_140px_140px_auto] gap-2 sm:gap-3 items-center" data-testid={`grant-row-${g.grant_id}`}>
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold truncate">{g.user_name || '—'}</div>
                    <div className="flex items-center gap-1.5 text-[11px] text-foreground/55 truncate">
                      <Mail className="h-3 w-3 shrink-0" />
                      <span className="truncate">{g.user_email}</span>
                    </div>
                    {g.note && <div className="text-[11px] text-foreground/45 italic truncate mt-0.5">{g.note}</div>}
                  </div>
                  <div>
                    <Badge variant="outline" className="border-foreground/15 text-foreground/70 capitalize">{g.package_label || g.package}</Badge>
                  </div>
                  <div>
                    <Badge variant="outline" className="border-[#004C54]/30 text-[#004C54] capitalize">{g.tier}</Badge>
                  </div>
                  <div className="text-[12px] flex items-center gap-1.5 text-foreground/65">
                    <CalendarIcon className="h-3 w-3" />
                    {g.expires_at ? (
                      <span>
                        {new Date(g.expires_at).toLocaleDateString()}
                        {isActive && <span className="text-foreground/45"> · {daysLeft}d left</span>}
                      </span>
                    ) : '—'}
                  </div>
                  <div>
                    {(() => {
                      if (g.status === 'revoked') {
                        return <Badge variant="outline" className="border-destructive/30 text-destructive">Revoked</Badge>;
                      }
                      if (expired) {
                        return <Badge variant="outline" className="border-foreground/15 text-foreground/55">Expired</Badge>;
                      }
                      return <Badge className="bg-[#004C54] text-white border-0">Active</Badge>;
                    })()}
                  </div>
                  <div className="flex justify-end">
                    {isActive && (
                      <Button size="sm" variant="outline" onClick={() => revoke(g)} className="text-destructive hover:text-destructive border-destructive/30" data-testid={`grant-revoke-${g.grant_id}`}>
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Revoke
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </div>
  );
}
